import React, { ReactNode } from 'react';

/**
 * ErrorBoundary component catches JavaScript errors in its child component tree and displays a fallback UI.
 *
 * @remarks
 * Use this component to wrap parts of your app where you want to gracefully handle rendering errors.
 *
 * @example
 * <ErrorBoundary fallback={<div>Custom error message</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @param props.children - The child components to render.
 * @param props.fallback - Optional custom fallback UI to display on error.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render shows the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can log the error to an error reporting service here
     
    console.error('ErrorBoundary caught an error', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI if provided, else default message
      return this.props.fallback || (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center p-8">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Something went wrong.</h1>
          <p className="text-white/80">An unexpected error occurred. Please refresh the page or contact support if the problem persists.</p>
        </div>
      );
    }
    return this.props.children;
  }
} 